import { formatDate, StatusField } from '@amfintech/react-admin-ui';
import { createHelpers, createResource } from '@amfintech/refine-react-admin';

import { AccountType, ApprovalStatus, type AccountRequest } from '~/api';

import { resourceNames } from './resource-names';

const { defineFields, defineCardSection, defineShowPage } = createHelpers<AccountRequest>({
  resourceName: resourceNames.accountRequest,
});

export const formatApprovalStatus = (status: ApprovalStatus) => {
  switch (status) {
    case ApprovalStatus.Approved:
      return 'success';
    case ApprovalStatus.Rejected:
      return 'danger';
    default:
      return 'pending';
  }
};

export const leadGenerationResource = createResource({
  identifier: resourceNames.leadGeneration,
  name: resourceNames.accountRequest,
  label: 'Lead Generation',
  list: {
    downloadConfig: {
      filenamePrefix: 'LeadGen_AccountOpening',
    },
    filters: {
      collapsible: 'expand-by-default',
      permanent: [
        {
          field: 'type',
          operator: 'eq',
          value: AccountType.Corporate,
        },
      ],
    },
  },
  fields: defineFields([
    'id',
    'type',
    'name',
    'accountCreatedDate',
    'approvalStatus',
    'contactName',
    'contactNo',
    'contactEmail',
    'createdAt',
    'deletedAt',
    'idNo',
    'idType',
    'referenceNo',
    'modeOfContact',
    'updatedAt',
  ]),
  defaultValues: {},
  allowCreate: false,
  allowDownload: true,
  defaultSorter: [{ field: 'createdAt', order: 'desc' }],
  filterControls: {
    referenceNo: {
      type: 'text',
      config: {
        label: 'Reference Number',
      },
      operator: 'contains',
    },
    name: {
      type: 'text',
      config: {
        label: 'Company Name',
      },
      operator: 'contains',
    },
    createdAt: {
      type: 'daterange',
      config: {
        label: 'Application Date',
        disabled: {
          after: new Date(),
        },
      },
    },
  },
  columns: ({ LinkToDetails }) => [
    {
      id: 'referenceNo',
      header: 'Transaction Reference Number',
      accessorKey: 'referenceNo',
      cell: (data) => {
        const referenceNo = data.cell.getValue<string>();

        return (
          <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
            {referenceNo}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'name',
      header: 'Company Name',
      accessorKey: 'name',
    },
    {
      id: 'contactName',
      header: 'Contact Person Name',
      accessorKey: 'contactName',
    },
    {
      id: 'contactNo',
      header: 'Contact Number',
      accessorKey: 'contactNo',
    },
    {
      id: 'contactEmail',
      header: 'Contact Email',
      accessorKey: 'contactEmail',
    },
    {
      id: 'modeOfContact',
      header: 'Mode of Contact',
      accessorKey: 'modeOfContact',
    },
    {
      id: 'createdAt',
      header: 'Submitted Date',
      accessorKey: 'createdAt',
      cell: (data) => {
        const createdAt = data.cell.getValue<Date>();
        return <>{formatDate(createdAt, { formatType: 'dateAndTime' })}</>;
      },
    },
  ],
  allowEdit: false,
  allowDelete: false,
  show: defineShowPage({
    sections: [
      defineCardSection({
        title: 'Contact details',
        collapsible: 'expand-by-default',
        fields: [
          'referenceNo',
          'name',
          'contactName',
          'contactNo',
          'contactEmail',
          'modeOfContact',
          'createdAt',
        ],
        displays: {
          createdAt: {
            label: 'Submitted Date',
            span: 2,
            render: (resource) => {
              const createdAt = resource.createdAt;
              return <>{formatDate(createdAt, { formatType: 'dateAndTime' })}</>;
            },
          },
        },
      }),
    ],
  }),
});
